// EventFlow Translations - English (EN) & Georgian (KA)

export const translations = {
  en: {
    // Common
    appName: "EventFlow",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    copy: "Copy",
    share: "Share",
    done: "Done",
    
    // Auth
    welcomeBack: "Welcome back",
    createAccount: "Create account",
    signIn: "Sign In",
    signUp: "Sign Up",
    signOut: "Sign Out",
    email: "Email",
    password: "Password",
    fullName: "Full Name",
    continueWithGoogle: "Continue with Google",
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    haveInvitationLink: "Have an invitation link?",
    openItDirectly: "Open it directly",
    loginSubtitle: "Sign in to manage your events.",
    registerSubtitle: "Start sending invitations in minutes.",
    fullNamePlaceholder: "Jane Doe",
    emailPlaceholder: "you@example.com",
    orDivider: "or",
    // Consent line under the auth card. Shown in both modes because Google
    // sign-in creates an account from either one, and the Terms are a public
    // offer accepted by registering.
    authLegalPrefix: "By continuing, you agree to our",
    authLegalTerms: "Terms of Service",
    authLegalConnector: "and",
    authLegalPrivacy: "Privacy Policy",
    // Password recovery
    forgotPassword: "Forgot password?",
    resetPasswordTitle: "Reset your password",
    resetPasswordSubtitle:
      "Enter your account email. An administrator will reset your password and share a new one with you.",
    resetPasswordEmailLabel: "Account email",
    resetPasswordNoteLabel: "Message (optional)",
    resetPasswordNotePlaceholder: "Anything that helps us verify it's you",
    sendResetRequest: "Request reset",
    resetRequestSentTitle: "Request received",
    resetRequestSentBody:
      "If an account exists for that email, an administrator will reset the password and share the new one with you shortly.",
    brandEyebrow: "Events, beautifully run",
    brandHeadlinePrefix: "Every event",
    brandHeadlineMiddle: "has a",
    brandHeadlineStory: "story.",
    brandDescription: "Send invitations, track RSVPs, and collect gifts — all in one calm, beautiful place.",
    brandStatRsvp: "RSVP",
    brandStatRsvpLabel: "live tracking",
    brandStatBilingualLabel: "bilingual",
    brandStatFee: "5%",
    brandStatFeeLabel: "transfer fee",
    welcomeBackToast: "Welcome back!",
    accountCreatedToast: "Account created successfully!",
    authFailedToast: "Authentication failed",

    // Landing page
    heroCtaPrimary: "Create your event",
    heroFeatureRsvp: "RSVP tracking",
    heroFeatureGifts: "Gift wishlists",
    heroFeatureBilingual: "Georgian & English",
    previewTitle: "Everything your guests need, in one link.",
    previewBody: "Share a single invitation link. Guests RSVP, bring a plus-one, and chip in for gifts — on any phone, in their own language. No app, no account required.",
    previewPoint1: "Live RSVP tracking",
    previewPoint2: "Card payments for gifts",
    previewPoint3: "Works on every phone",
    stepsTitle: "Launch in minutes",
    stepsSubtitle: "From idea to sent invitation in three steps.",
    step1Title: "Create",
    step1Body: "Add your event details, pick a theme, and set up a gift wishlist.",
    step2Title: "Share",
    step2Body: "Send one link by WhatsApp or anywhere — guests open it instantly.",
    step3Title: "Celebrate",
    step3Body: "Track RSVPs live and receive gifts straight to your account.",
    ctaTitle: "Your next event starts here.",
    ctaBody: "Create your first event free — your guests can RSVP in seconds.",
    mockEventTitle: "Mariam's Birthday",

    // Dashboard
    welcomeBackUser: "Welcome back",
    manageEvents: "Manage your events and track RSVPs",
    totalEvents: "Total Events",
    totalGuests: "Total Guests",
    attending: "Attending",
    pending: "Pending",
    yourEvents: "Your Events",
    createEvent: "Create Event",
    createFirstEvent: "Create Your First Event",
    noEventsYet: "No events yet",
    createFirstEventDesc: "Create your first event to start inviting guests",
    viewDetails: "View Details",
    
    // Event Creation
    createEventTitle: "Create Event",
    editEvent: "Edit Event",
    eventTitle: "Event Title",
    eventTitlePlaceholder: "e.g., Sarah's Birthday Party",
    eventTitleHint: "The headline guests see at the top of the invitation.",
    eventType: "Event Type",
    selectEventType: "Select event type",
    date: "Date",
    time: "Time",
    location: "Location",
    locationPlaceholder: "Search or enter address",
    description: "Description",
    descriptionPlaceholder: "Tell your guests what to expect...",
    coverImage: "Cover Image",
    uploadImage: "Upload Image",
    dragDropImage: "Drag & drop or click to upload",
    theme: "Theme",
    language: "Language",
    rsvpDeadline: "RSVP Deadline",
    rsvpDeadlineHint: "Guests won't be able to RSVP after this date",
    
    // Themes
    themeClassic: "Classic Elegant",
    themeModern: "Modern Minimal",
    themeRomantic: "Romantic Floral",
    themeDark: "Dark Luxury",
    themeKids: "Kids Celebration",
    
    // Event Types
    birthdayParty: "Birthday Party",
    wedding: "Wedding",
    babyShower: "Baby Shower",
    corporateEvent: "Corporate Event",
    dinnerParty: "Dinner Party",
    anniversary: "Anniversary",
    graduation: "Graduation",
    holidayCelebration: "Holiday Celebration",
    other: "Other",
    
    // Guests
    guests: "Guests",
    guestList: "Guest List",
    addGuest: "Add Guest",
    editGuest: "Edit Guest",
    guestName: "Guest Name",
    guestNamePlaceholder: "Enter guest name",
    phone: "Phone",
    allowPlusOne: "Allow plus one",
    inviteStatus: "Invite Status",
    rsvpStatus: "RSVP Status",
    markSent: "Mark Sent",
    noGuestsYet: "No guests added yet",
    
    // RSVP
    willYouAttend: "Will you join us?",
    iWillAttend: "Yes, I'll attend",
    iCantAttend: "No, I can't",
    maybe: "Maybe",
    // RSVP response buttons (defaults; organizer can override per event)
    rsvpAttend: "I'm coming",
    rsvpMaybe: "Maybe",
    rsvpDecline: "Can't make it",
    yourName: "Your Name",
    enterYourName: "Enter your name",
    plusOnesQuestion: "Bringing anyone with you?",
    plusOnesNone: "Just me",
    messageOptional: "Message (optional)",
    messageToHost: "Any message for the host...",
    confirmRsvp: "Send RSVP",
    thanksForResponding: "Thanks for responding!",
    cantWaitToSeeYou: "We can't wait to see you there!",
    hopeYouCanMakeIt: "Hope you can make it!",
    sorryYouCantMake: "Sorry you can't make it. We'll miss you!",
    rsvpClosed: "RSVP Closed",
    rsvpDeadlinePassed: "The RSVP deadline has passed",
    
    // Wishlist
    wishlist: "Wishlist",
    addItem: "Add Item",
    itemTitle: "Item Title",
    itemTitlePlaceholder: "e.g., New Camera",
    targetAmount: "Target Amount",
    noWishlistItems: "No wishlist items yet",
    contribute: "Contribute",
    funded: "Funded",
    remaining: "remaining",
    contributorName: "Your Name",
    anonymous: "Anonymous",
    amount: "Amount",
    simulatedContribution: "This is a simulated contribution (no real payment)",
    thankYouContribution: "Thank You!",
    contributionRecorded: "Your contribution has been recorded.",
    alreadyFunded: "This item is already fully funded",
    
    // Invitation Page
    invitation: "Invitation",
    event: "Event",
    youreInvited: "You're Invited",
    guestOfHonor: "Guest of honor",
    gettingThere: "Getting there",
    openInGoogleMaps: "Open in Google Maps",
    addToCalendar: "Add to Calendar",
    googleCalendar: "Google Calendar",
    appleCalendar: "Apple Calendar",
    shareViaWhatsApp: "Share via WhatsApp",
    copyInviteMessage: "Copy Invite Message",
    inviteLinkCopied: "Invitation link copied!",
    inviteMessageCopied: "Invite message copied!",
    
    // Countdown
    daysRemaining: "days remaining",
    today: "Today",
    tomorrow: "Tomorrow",
    eventPassed: "Event has passed",
    
    // Sharing
    shareInvitation: "Share Invitation",
    copyLink: "Copy Link",

    // Account (user balance) & referrals
    account: "Account",
    accountBalance: "Account balance",
    accountSubtitle: "Money you hold with Storyfy, kept separately per currency.",
    accountEmpty: "Nothing here yet",
    accountEmptyBody: "Funds from closed events and referral earnings land here.",
    accountHistory: "Transaction history",
    accountNoHistory: "No transactions yet",
    balanceAvailable: "Available",
    entryEventClosure: "Closed event",
    entryReferral: "Referral bonus",
    entryPayout: "Payout",
    entryAdjustment: "Adjustment",
    balanceAfter: "Balance after",
    loadMore: "Load more",
    referral: "Referral",
    referralTitle: "Invite friends, earn on their events",
    referralBody:
      "Share your code or link. When someone signs up with it, you earn a share of every gift their events collect — at no cost to them.",
    referralCode: "Your referral code",
    referralLink: "Your referral link",
    referralSignups: "Signups",
    referralEarned: "Earned",
    referralPayouts: "Payouts",
    referralCodeCopied: "Referral code copied!",
    referralLinkCopied: "Referral link copied!",
    referralCodeLabel: "Referral code (optional)",
    referralCodePlaceholder: "e.g. K7M2XQ4P",
    referralCodeValid: "Referral code applied",
    referralCodeFrom: "Referred by {name}",
    referralCodeUnknown: "We don't recognise that code — you can still sign up.",

    // RSVP controls
    rsvpControls: "RSVP controls",
    closeRsvpNow: "Close RSVPs now",
    closeRsvpNowHelp:
      "Stops new responses immediately, whatever the deadline says. Turn it off to reopen.",
    inviteOnly: "Invite-only RSVP",
    inviteOnlyHelp:
      "Only people on your guest list can respond, using the personal code you send them.",
    guestCapacity: "Guest limit",
    guestCapacityHelp:
      "Maximum confirmed guests, counting companions. RSVPs close automatically once it's reached. Leave empty for no limit.",
    guestCapacityPlaceholder: "No limit",
    inviteCode: "Invite code",
    inviteCodeCopied: "Invite code copied!",
    yourInviteCode: "Your invite code",
    inviteCodePlaceholder: "Enter your code",
    inviteCodeRequired: "This invitation needs your personal code",
    rsvpClosedTitle: "RSVPs are closed",
    rsvpClosedByHost: "The host has closed responses for this event.",
    rsvpClosedDeadline: "The RSVP deadline has passed.",
    rsvpClosedFull: "This event has reached its guest limit.",
    placesLeft: "{n} places left",
    onePlaceLeft: "1 place left",

    // Deleting an event
    deleteEventTitle: "Delete this event?",
    deleteEventBody:
      "This permanently removes the event, its guest list and its invitation link. This cannot be undone.",
    deleteEventFunds: "These funds move to your account balance:",
    deleteEventConfirm: "Delete event",
    deleteMediaTitle: "The album goes too",
    deleteMediaBody:
      "Deleting this event also deletes every photo and video guests uploaded to its album.",
    deleteMediaNoCopy:
      "We hold the only copy — once deleted they cannot be recovered. Download anything you want to keep first.",
    eventDeletedWithFunds: "Event deleted — {amount} moved to your account",
    // Event wallet balance chip (hidden until the host reveals it)
    walletBalance: "Balance",
    showBalance: "Show balance",
    hideBalance: "Hide balance",


    // Admin
    admin: "Admin",
    adminPanel: "Admin Panel",
    overview: "Overview",
    users: "Users",
    events: "Events",
    rsvps: "RSVPs",
    contributions: "Contributions",
    recentUsers: "Recent Users",
    recentEvents: "Recent Events",
    
    // Status
    sent: "Sent",
    declined: "Declined",
    
    // Errors
    invitationNotFound: "Invitation Not Found",
    invitationExpired: "This invitation link may have expired or is invalid.",
    errorOccurred: "An error occurred",
    failedToLoad: "Failed to load",
    
    // Footer
    poweredBy: "Powered by Storyfy.ge",
    footerPoweredBy: "Powered by Storyfy.ge",
    footerCopyright: "© 2026 Storyfy.ge. All rights reserved.",
    footerTerms: "Terms of Service",
    footerPrivacy: "Privacy Policy",
    footerRefund: "Refund Policy",
    footerContact: "Contact:",
    footerTagline: "Plan events, collect RSVPs, and receive gifts — all in one place.",
    footerLegal: "Legal",
    footerCompany: "Company",
    footerSocial: "Follow us",
    footerContactUs: "Contact us",
    footerWebsite: "Website",
  },
  
  ka: {
    // Common
    appName: "EventFlow",
    loading: "იტვირთება...",
    save: "შენახვა",
    cancel: "გაუქმება",
    delete: "წაშლა",
    edit: "რედაქტირება",
    close: "დახურვა",
    copy: "კოპირება",
    share: "გაზიარება",
    done: "მზადაა",
    
    // Auth
    welcomeBack: "კეთილი იყოს შენი დაბრუნება",
    createAccount: "ანგარიშის შექმნა",
    signIn: "შესვლა",
    signUp: "რეგისტრაცია",
    signOut: "გასვლა",
    email: "ელ-ფოსტა",
    password: "პაროლი",
    fullName: "სახელი და გვარი",
    continueWithGoogle: "Google-ით გაგრძელება",
    dontHaveAccount: "არ გაქვს ანგარიში?",
    alreadyHaveAccount: "უკვე გაქვს ანგარიში?",
    haveInvitationLink: "გაქვს მოწვევის ბმული?",
    openItDirectly: "გახსენი პირდაპირ",
    loginSubtitle: "შედი შენი ღონისძიებების სამართავად.",
    registerSubtitle: "დაიწყე მოწვევების გაგზავნა წუთებში.",
    fullNamePlaceholder: "ნინო ნინიძე",
    emailPlaceholder: "you@example.com",
    orDivider: "ან",
    // Link labels carry the dative endings the sentence needs — they are not
    // the nominative footer labels, so don't reuse footerTerms/footerPrivacy.
    authLegalPrefix: "გაგრძელებით თქვენ ეთანხმებით ჩვენს",
    authLegalTerms: "მომსახურების პირობებსა",
    authLegalConnector: "და",
    authLegalPrivacy: "კონფიდენციალურობის პოლიტიკას",
    // Password recovery
    forgotPassword: "დაგავიწყდა პაროლი?",
    resetPasswordTitle: "პაროლის აღდგენა",
    resetPasswordSubtitle:
      "შეიყვანე შენი ანგარიშის ელფოსტა. ადმინისტრატორი აღადგენს პაროლს და გამოგიგზავნის ახალს.",
    resetPasswordEmailLabel: "ანგარიშის ელფოსტა",
    resetPasswordNoteLabel: "შეტყობინება (არასავალდებულო)",
    resetPasswordNotePlaceholder: "ნებისმიერი დეტალი, რომელიც დაგვეხმარება ვინაობის დადასტურებაში",
    sendResetRequest: "აღდგენის მოთხოვნა",
    resetRequestSentTitle: "მოთხოვნა მიღებულია",
    resetRequestSentBody:
      "თუ ამ ელფოსტაზე ანგარიში არსებობს, ადმინისტრატორი მალე აღადგენს პაროლს და გამოგიგზავნის ახალს.",
    brandEyebrow: "ღონისძიებები, ლამაზად მართული",
    brandHeadlinePrefix: "ყველა ღონისძიებას",
    brandHeadlineMiddle: "აქვს თავისი",
    brandHeadlineStory: "ისტორია.",
    brandDescription: "გააგზავნე მოწვევები, თვალი ადევნე დადასტურებებს და შეაგროვე საჩუქრები — ერთ მშვიდ და ლამაზ სივრცეში.",
    brandStatRsvp: "RSVP",
    brandStatRsvpLabel: "ცოცხალი თვალთვალი",
    brandStatBilingualLabel: "ორენოვანი",
    brandStatFee: "5%",
    brandStatFeeLabel: "გადარიცხვის საკომისიო",
    welcomeBackToast: "კეთილი იყოს შენი დაბრუნება!",
    accountCreatedToast: "ანგარიში წარმატებით შეიქმნა!",
    authFailedToast: "ავტორიზაცია ვერ მოხერხდა",

    // Landing page
    heroCtaPrimary: "შექმენი ღონისძიება",
    heroFeatureRsvp: "დასწრების აღრიცხვა",
    heroFeatureGifts: "სასაჩუქრე სია",
    heroFeatureBilingual: "ქართული და ინგლისური",
    previewTitle: "ყველაფერი, რაც სტუმარს სჭირდება — ერთ ბმულში.",
    previewBody: "გააზიარე ერთი მოწვევის ბმული. სტუმრები ადასტურებენ დასწრებას, ამატებენ თანმხლებ პირს და მონაწილეობენ საჩუქრის დაფინანსებაში — ნებისმიერ ტელეფონზე, საკუთარ ენაზე. აპლიკაცია ან ანგარიში არ სჭირდებათ.",
    previewPoint1: "დასწრების ცოცხალი აღრიცხვა",
    previewPoint2: "ბარათით გადახდა საჩუქრებზე",
    previewPoint3: "მუშაობს ნებისმიერ ტელეფონზე",
    stepsTitle: "გაშვება წუთებში",
    stepsSubtitle: "იდეიდან გაგზავნილ მოწვევამდე — სამ ნაბიჯში.",
    step1Title: "შექმენი",
    step1Body: "დაამატე ღონისძიების დეტალები, აირჩიე თემა და შექმენი სასაჩუქრე სია.",
    step2Title: "გააზიარე",
    step2Body: "გააგზავნე ერთი ბმული WhatsApp-ით ან სადმე — სტუმრები მყისიერად ხსნიან.",
    step3Title: "იზეიმე",
    step3Body: "თვალი ადევნე დადასტურებებს და მიიღე საჩუქრები პირდაპირ ანგარიშზე.",
    ctaTitle: "შენი შემდეგი ღონისძიება აქ იწყება.",
    ctaBody: "შექმენი პირველი ღონისძიება უფასოდ — სტუმრები წამებში დაადასტურებენ.",
    mockEventTitle: "მარიამის დაბადების დღე",

    // Dashboard
    welcomeBackUser: "გამარჯობა",
    manageEvents: "მართე შენი ღონისძიებები",
    totalEvents: "ღონისძიება",
    totalGuests: "სტუმრები",
    attending: "დასწრება",
    pending: "მოლოდინში",
    yourEvents: "შენი ღონისძიებები",
    createEvent: "შექმნა",
    createFirstEvent: "შექმენი პირველი ღონისძიება",
    noEventsYet: "ჯერ არ გაქვს ღონისძიება",
    createFirstEventDesc: "შექმენი ღონისძიება სტუმრების მოსაწვევად",
    viewDetails: "დეტალები",
    
    // Event Creation
    createEventTitle: "შექმნა",
    editEvent: "ღონისძიების რედაქტირება",
    eventTitle: "ღონისძიების სათაური",
    eventTitlePlaceholder: "მაგ., საშას დაბადების დღე",
    eventTitleHint: "სათაური, რომელსაც სტუმრები მოწვევის თავში ხედავენ.",
    eventType: "ღონისძიების ტიპი",
    selectEventType: "აირჩიე ტიპი",
    date: "თარიღი",
    time: "დრო",
    location: "მისამართი",
    locationPlaceholder: "მოძებნე ან შეიყვანე მისამართი",
    description: "აღწერა",
    descriptionPlaceholder: "რას უნდა ელოდონ სტუმრები...",
    coverImage: "ფონის სურათი",
    uploadImage: "სურათის ატვირთვა",
    dragDropImage: "გადმოიტანე ან დააჭირე ასატვირთად",
    theme: "თემა",
    language: "ენა",
    rsvpDeadline: "დადასტურების ვადა",
    rsvpDeadlineHint: "ამ თარიღის შემდეგ ვეღარ დაადასტურებენ",
    
    // Themes
    themeClassic: "კლასიკური ელეგანტური",
    themeModern: "თანამედროვე მინიმალისტური",
    themeRomantic: "რომანტიული ყვავილოვანი",
    themeDark: "მუქი ლუქსი",
    themeKids: "საბავშვო ზეიმი",
    
    // Event Types
    birthdayParty: "დაბადების დღე",
    wedding: "ქორწილი",
    babyShower: "ბეიბი შაუერი",
    corporateEvent: "კორპორატიული ღონისძიება",
    dinnerParty: "სადილი",
    anniversary: "იუბილე",
    graduation: "გამოსაშვები",
    holidayCelebration: "დღესასწაული",
    other: "სხვა",
    
    // Guests
    guests: "სტუმრები",
    guestList: "სტუმრების სია",
    addGuest: "სტუმრის დამატება",
    editGuest: "სტუმრის რედაქტირება",
    guestName: "სტუმრის სახელი",
    guestNamePlaceholder: "შეიყვანე სახელი",
    phone: "ტელეფონი",
    allowPlusOne: "+1 სტუმრის დაშვება",
    inviteStatus: "მოწვევის სტატუსი",
    rsvpStatus: "დადასტურების სტატუსი",
    markSent: "გაგზავნილად მონიშვნა",
    noGuestsYet: "სტუმრები ჯერ არ დამატებულა",
    
    // RSVP
    willYouAttend: "შემოგვიერთდები?",
    iWillAttend: "დავესწრები",
    iCantAttend: "ვერ დავესწრები",
    maybe: "შეიძლება",
    // RSVP response buttons (defaults; organizer can override per event)
    rsvpAttend: "მოვდივარ",
    rsvpMaybe: "ალბათ",
    rsvpDecline: "ვერ მოვალ",
    yourName: "შენი სახელი",
    enterYourName: "შეიყვანე სახელი",
    plusOnesQuestion: "თანმხლები სტუმრები მოგყავს?",
    plusOnesNone: "მხოლოდ მე",
    messageOptional: "შეტყობინება (არასავალდებულო)",
    messageToHost: "შეტყობინება მასპინძლისთვის...",
    confirmRsvp: "გაგზავნა",
    thanksForResponding: "მადლობა პასუხისთვის!",
    cantWaitToSeeYou: "გელოდებით!",
    hopeYouCanMakeIt: "იმედია მოხვალ!",
    sorryYouCantMake: "სამწუხაროა რომ ვერ მოხვალ!",
    rsvpClosed: "დადასტურება დასრულდა",
    rsvpDeadlinePassed: "დადასტურების ვადა ამოიწურა",
    
    // Wishlist
    wishlist: "სურვილების სია",
    addItem: "დამატება",
    itemTitle: "დასახელება",
    itemTitlePlaceholder: "მაგ., ახალი კამერა",
    targetAmount: "სამიზნე თანხა",
    noWishlistItems: "სურვილების სია ცარიელია",
    contribute: "დაფინანსება",
    funded: "შევსებულია",
    remaining: "დარჩენილი თანხა",
    contributorName: "შენი სახელი",
    anonymous: "ანონიმური",
    amount: "თანხა",
    simulatedContribution: "ეს სიმულირებული კონტრიბუციაა (რეალური გადახდა არ არის)",
    thankYouContribution: "მადლობა!",
    contributionRecorded: "შენი კონტრიბუცია ჩაწერილია.",
    alreadyFunded: "ეს ნივთი უკვე შევსებულია",
    
    // Invitation Page
    invitation: "მოწვევა",
    event: "ღონისძიება",
    youreInvited: "მოწვეული ხართ",
    guestOfHonor: "ღირსეული სტუმარი",
    gettingThere: "როგორ მოხვიდე",
    openInGoogleMaps: "Google Maps-ში გახსნა",
    addToCalendar: "კალენდარში დამატება",
    googleCalendar: "Google Calendar",
    appleCalendar: "Apple Calendar",
    shareViaWhatsApp: "WhatsApp-ით გაზიარება",
    copyInviteMessage: "მოწვევის კოპირება",
    inviteLinkCopied: "ბმული დაკოპირდა!",
    inviteMessageCopied: "შეტყობინება დაკოპირდა!",
    
    // Countdown
    daysRemaining: "დღე დარჩა",
    today: "დღეს",
    tomorrow: "ხვალ",
    eventPassed: "ღონისძიება გავიდა",
    
    // Sharing
    shareInvitation: "მოწვევის გაზიარება",
    copyLink: "კოპირება",

    // Account (user balance) & referrals
    account: "ანგარიში",
    accountBalance: "ანგარიშის ბალანსი",
    accountSubtitle: "შენი თანხა Storyfy-ზე, თითოეული ვალუტა ცალკე ინახება.",
    accountEmpty: "ჯერ ცარიელია",
    accountEmptyBody: "დახურული ღონისძიებების თანხა და რეფერალის შემოსავალი აქ აისახება.",
    accountHistory: "ტრანზაქციების ისტორია",
    accountNoHistory: "ტრანზაქციები ჯერ არ არის",
    balanceAvailable: "ხელმისაწვდომი",
    entryEventClosure: "დახურული ღონისძიება",
    entryReferral: "რეფერალის ბონუსი",
    entryPayout: "გატანა",
    entryAdjustment: "კორექტირება",
    balanceAfter: "ბალანსი",
    loadMore: "მეტის ჩვენება",
    referral: "რეფერალი",
    referralTitle: "მოიწვიე მეგობრები და გამოიმუშავე",
    referralBody:
      "გააზიარე შენი კოდი ან ბმული. როცა ვინმე მისით დარეგისტრირდება, მისი ღონისძიებების ყოველი საჩუქრიდან შენც გერგება წილი — მისთვის დამატებითი ხარჯის გარეშე.",
    referralCode: "შენი რეფერალის კოდი",
    referralLink: "შენი რეფერალის ბმული",
    referralSignups: "რეგისტრაცია",
    referralEarned: "გამომუშავებული",
    referralPayouts: "დარიცხვები",
    referralCodeCopied: "კოდი დაკოპირდა!",
    referralLinkCopied: "ბმული დაკოპირდა!",
    referralCodeLabel: "რეფერალის კოდი (არასავალდებულო)",
    referralCodePlaceholder: "მაგ. K7M2XQ4P",
    referralCodeValid: "კოდი მიღებულია",
    referralCodeFrom: "მოგიწვია {name}-მ",
    referralCodeUnknown: "ასეთი კოდი ვერ ვიპოვეთ — რეგისტრაცია მაინც შეგიძლია.",

    // RSVP controls
    rsvpControls: "RSVP-ის მართვა",
    closeRsvpNow: "RSVP-ის დახურვა",
    closeRsvpNowHelp:
      "მაშინვე წყვეტს ახალ პასუხებს, თარიღის მიუხედავად. გამორთვით ისევ გაიხსნება.",
    inviteOnly: "მხოლოდ მოწვეულებისთვის",
    inviteOnlyHelp:
      "პასუხის გაცემა მხოლოდ შენს სიაში მყოფებს შეუძლიათ, შენს მიერ გაგზავნილი პირადი კოდით.",
    guestCapacity: "სტუმრების ლიმიტი",
    guestCapacityHelp:
      "დადასტურებული სტუმრების მაქსიმალური რაოდენობა, თანმხლებების ჩათვლით. ლიმიტის შევსებისას RSVP ავტომატურად იხურება. ცარიელი = ულიმიტო.",
    guestCapacityPlaceholder: "ულიმიტო",
    inviteCode: "მოწვევის კოდი",
    inviteCodeCopied: "კოდი დაკოპირდა!",
    yourInviteCode: "შენი მოწვევის კოდი",
    inviteCodePlaceholder: "შეიყვანე კოდი",
    inviteCodeRequired: "ამ მოწვევას პირადი კოდი სჭირდება",
    rsvpClosedTitle: "RSVP დახურულია",
    rsvpClosedByHost: "მასპინძელმა პასუხების მიღება დახურა.",
    rsvpClosedDeadline: "დადასტურების ვადა გავიდა.",
    rsvpClosedFull: "ღონისძიებამ სტუმრების ლიმიტს მიაღწია.",
    placesLeft: "დარჩა {n} ადგილი",
    onePlaceLeft: "დარჩა 1 ადგილი",

    // Deleting an event
    deleteEventTitle: "წავშალოთ ეს ღონისძიება?",
    deleteEventBody:
      "სამუდამოდ წაიშლება ღონისძიება, სტუმრების სია და მოწვევის ბმული. მოქმედება შეუქცევადია.",
    deleteEventFunds: "ეს თანხა გადავა შენს ანგარიშზე:",
    deleteEventConfirm: "წაშლა",
    deleteMediaTitle: "ალბომიც წაიშლება",
    deleteMediaBody:
      "ღონისძიების წაშლით წაიშლება ყველა ფოტო და ვიდეო, რაც სტუმრებმა ალბომში ატვირთეს.",
    deleteMediaNoCopy:
      "ერთადერთი ასლი ჩვენთანაა — წაშლის შემდეგ აღდგენა შეუძლებელია. ჯერ ჩამოტვირთე ის, რაც გინდა რომ დაგრჩეს.",
    eventDeletedWithFunds: "ღონისძიება წაიშალა — {amount} გადავიდა შენს ანგარიშზე",
    // Event wallet balance chip (hidden until the host reveals it)
    walletBalance: "ბალანსი",
    showBalance: "ბალანსის ჩვენება",
    hideBalance: "ბალანსის დამალვა",


    // Admin
    admin: "ადმინი",
    adminPanel: "ადმინ პანელი",
    overview: "მიმოხილვა",
    users: "მომხმარებლები",
    events: "ღონისძიებები",
    rsvps: "დადასტურებები",
    contributions: "კონტრიბუციები",
    recentUsers: "ბოლო მომხმარებლები",
    recentEvents: "ბოლო ღონისძიებები",
    
    // Status
    sent: "გაგზავნილი",
    declined: "უარყოფილი",
    
    // Errors
    invitationNotFound: "მოწვევა ვერ მოიძებნა",
    invitationExpired: "მოწვევის ბმული არასწორია ან ვადა გასულია.",
    errorOccurred: "დაფიქსირდა შეცდომა",
    failedToLoad: "ვერ ჩაიტვირთა",
    
    // Footer
    poweredBy: "Storyfy.ge-ით",
    footerPoweredBy: "Storyfy.ge-ით",
    footerCopyright: "© 2026 Storyfy.ge. ყველა უფლება დაცულია.",
    footerTerms: "მომსახურების წესები და პირობები",
    footerPrivacy: "კონფიდენციალურობის პოლიტიკა",
    footerRefund: "თანხის დაბრუნების პოლიტიკა",
    footerContact: "საკონტაქტო:",
    footerTagline: "დაგეგმე ღონისძიება, შეაგროვე დასწრების დასტური და მიიღე საჩუქრები — ერთ სივრცეში.",
    footerLegal: "სამართლებრივი",
    footerCompany: "კომპანია",
    footerSocial: "გამოგვყევით",
    footerContactUs: "დაგვიკავშირდი",
    footerWebsite: "ვებსაიტი",
  }
};

// Date formatting based on language
export const formatDate = (dateStr, language = 'en') => {
  const date = new Date(dateStr);

  // Date-only ISO strings ("YYYY-MM-DD") are parsed as UTC midnight, so reading
  // local-time parts would shift the day backwards for negative-UTC-offset
  // viewers. Use UTC accessors to display the intended calendar date everywhere.
  if (language === 'ka') {
    const months = ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
                    'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'];
    return `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

// Format an event's day or day-range: "5 January 2026" or "5 January 2026 – 7 January 2026".
export const formatDateRange = (startStr, endStr, language = 'en') => {
  if (!startStr) return '';
  if (!endStr || endStr === startStr) return formatDate(startStr, language);
  return `${formatDate(startStr, language)} – ${formatDate(endStr, language)}`;
};

// Format a time or time-range: "18:00" or "18:00 – 22:00".
export const formatTimeRange = (startTime, endTime) => {
  if (!startTime) return endTime || '';
  if (!endTime) return startTime;
  return `${startTime} – ${endTime}`;
};

export default translations;
